import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Info, Pin, Search, Trash2, X } from 'lucide-react'
import { filterPackRows, lineupReasonLabel, LINEUP_STATUS_LABELS, recipeRows } from './prepackLineupModel'
import { useLineupPanel } from './useLineupPanel'

// One column template for the header and every row, with no content-sized track.
const PACK_COLUMNS = 'grid-cols-[minmax(0,1fr)_56px_92px_36px]'
const PACK_COLUMNS_REMOVABLE = 'grid-cols-[minmax(0,1fr)_56px_92px_36px_24px]'

const STATUS_TONE = {
  live: { text: 'text-[var(--wf-color-success)]', dot: 'bg-[var(--wf-color-success)]' },
  available: { text: 'text-[var(--prepacks-text)]', dot: 'bg-[var(--prepacks-muted)]' },
  excluded: { text: 'text-[var(--prepacks-muted)]', dot: 'bg-[var(--prepacks-border)]' },
  unavailable: { text: 'text-[var(--wf-color-warning)]', dot: 'bg-[var(--wf-color-warning)]' },
}

/** The page's switch, compact: a button with the switch role. */
export function LineupSwitch({ checked, onChange, label, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-4 w-7 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${checked ? 'bg-[var(--prepacks-accent)]' : 'bg-[var(--prepacks-border)]'}`}
    >
      <span className={`absolute left-0.5 top-0.5 h-3 w-3 rounded-full bg-[var(--wf-color-on-action)] transition-transform ${checked ? 'translate-x-3' : ''}`} />
    </button>
  )
}

function SearchBox({ label, value, onChange, className = '' }) {
  return (
    <label className={`flex items-center gap-1.5 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2 py-1 text-xs ${className}`}>
      <Search className="h-3 w-3 shrink-0 text-[var(--prepacks-muted)]" aria-hidden="true" />
      <input type="search" aria-label={label} placeholder={`${label}…`} value={value} onChange={(event) => onChange(event.target.value)} className="min-w-0 flex-1 bg-transparent text-[var(--wf-color-text)] outline-none placeholder:text-[var(--prepacks-muted)]" />
    </label>
  )
}

/**
 * Connected recipes (#990): the schedules feeding the surface, each with its
 * switch and, expanded, its packs as compact rows with their status here and
 * their own switch. Only the selected surface is affected. With onRemovePack
 * every row can also be removed from the surface: the cards show only live
 * and waiting packs, so this is where an expired or retargeted assignment
 * is removed.
 */
export function SurfaceRecipesPanel({ view, packsById, surfaceName, canConfigure, busy, onToggleRecipe, onTogglePacks, onOpenPack, onRemovePack, onOpenMix, onClose }) {
  const { ref } = useLineupPanel({ inlineQuery: '(min-width: 1024px)', onClose })
  const [search, setSearch] = useState('')
  // Rows open on a click; while searching every row is open until closed.
  const [expanded, setExpanded] = useState(() => new Set())
  const [collapsed, setCollapsed] = useState(() => new Set())
  useEffect(() => { setCollapsed(new Set()) }, [search])
  const searching = Boolean(search.trim())
  const rows = useMemo(() => recipeRows(view, packsById, search), [view, packsById, search])
  const toggle = (key) => (searching ? setCollapsed : setExpanded)((current) => {
    const next = new Set(current)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })
  const isOpen = (key) => (searching ? !collapsed.has(key) : expanded.has(key))
  return (
    <aside ref={ref} tabIndex={-1} aria-label="Connected recipes" className="fixed inset-0 z-40 flex flex-col bg-[var(--prepacks-surface)] text-[var(--wf-color-text)] lg:static lg:z-auto lg:w-[400px] lg:shrink-0 lg:border-l lg:border-[var(--prepacks-border)]">
      <header className="flex items-center gap-2 border-b border-[var(--prepacks-border)] px-4 py-3">
        <h2 className="text-sm font-semibold">Connected recipes</h2>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={onOpenMix} className="rounded border border-[var(--prepacks-accent-text)] px-2.5 py-1 text-xs text-[var(--prepacks-accent-text)] hover:bg-[var(--wf-color-action-soft)]">Manage mix</button>
          <button type="button" aria-label="Close connected recipes" onClick={onClose} className="rounded p-1 text-[var(--prepacks-muted)] hover:text-[var(--wf-color-text)]"><X className="h-4 w-4" /></button>
        </div>
      </header>
      <div className="px-4 py-3"><SearchBox label="Search schedules" value={search} onChange={setSearch} /></div>
      <ul aria-busy={!view || undefined} className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {!view ? <li className="px-1 py-6 text-center text-xs text-[var(--prepacks-muted)]">Loading this surface’s schedules…</li> : rows.length === 0 ? <li className="px-1 py-6 text-center text-xs text-[var(--prepacks-muted)]">{search ? 'No schedule or pack matches.' : 'No schedule feeds this surface yet.'}</li> : null}
        {rows.map((recipe) => {
          const key = recipe.recipe_id || 'manual'
          return (
            <RecipeRow
              key={key}
              recipe={recipe}
              open={isOpen(key)}
              onExpand={() => toggle(key)}
              canConfigure={canConfigure}
              busy={busy}
              onToggleRecipe={onToggleRecipe}
              onTogglePacks={onTogglePacks}
              onOpenPack={onOpenPack}
              onRemovePack={onRemovePack}
            />
          )
        })}
      </ul>
      <footer className="flex items-center gap-1.5 border-t border-[var(--prepacks-border)] px-4 py-2 text-[11px] text-[var(--prepacks-muted)]"><Info className="h-3.5 w-3.5" aria-hidden="true" />Only affects {surfaceName}.</footer>
    </aside>
  )
}

function RecipeRow({ recipe, open, onExpand, canConfigure, busy, onToggleRecipe, onTogglePacks, onOpenPack, onRemovePack }) {
  const [packSearch, setPackSearch] = useState('')
  const packs = useMemo(() => filterPackRows(recipe.packs, packSearch), [recipe.packs, packSearch])
  const name = recipe.recipe_id ? recipe.name : 'Manually added'
  const regionId = `recipe-packs-${recipe.recipe_id || 'manual'}`
  return (
    <li className={`mb-2 rounded-lg border bg-[var(--prepacks-bg)] ${open ? 'border-[var(--prepacks-accent-text)]' : 'border-[var(--prepacks-border)]'}`}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button type="button" aria-expanded={open} aria-controls={regionId} onClick={onExpand} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
          <span className={`truncate text-sm font-semibold ${recipe.enabled ? '' : 'text-[var(--prepacks-muted)]'}`}>{name}</span>
          {recipe.deleted ? <span className="shrink-0 rounded bg-[var(--prepacks-raised)] px-1.5 text-[10px] text-[var(--prepacks-muted)]">Deleted schedule</span> : null}
          <span className="ml-auto shrink-0 text-[11px] text-[var(--prepacks-muted)]">{recipe.counts?.total ?? 0} packs · {recipe.counts?.displayed ?? 0} displayed</span>
        </button>
        {recipe.recipe_id ? <LineupSwitch label={`${name} on this surface`} checked={recipe.enabled} disabled={!canConfigure || busy} onChange={(enabled) => onToggleRecipe(recipe.recipe_id, enabled)} /> : null}
      </div>
      {open ? (
        <div id={regionId} className="border-t border-[var(--prepacks-border)]">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-[11px]">
            <span className="font-semibold">{packs.length} packs</span>
            {canConfigure && packs.length > 0 ? (
              <>
                <button type="button" disabled={busy || !recipe.enabled} onClick={() => onTogglePacks(packs.map((pack) => pack.id), true)} className="text-[var(--prepacks-accent-text)] disabled:opacity-40">All on</button>
                <span aria-hidden="true" className="text-[var(--prepacks-border)]">|</span>
                <button type="button" disabled={busy || !recipe.enabled} onClick={() => onTogglePacks(packs.map((pack) => pack.id), false)} className="text-[var(--prepacks-accent-text)] disabled:opacity-40">All off</button>
              </>
            ) : null}
            <SearchBox label={`Search packs in ${name}`} value={packSearch} onChange={setPackSearch} className="ml-auto w-44" />
          </div>
          {!recipe.enabled ? <p className="px-3 pb-2 text-[11px] text-[var(--prepacks-muted)]">Off for this surface: its packs can’t show here. Each pack keeps its own switch for when the schedule is back on.</p> : null}
          <div role="table" aria-label={`${name} packs`} className="max-h-[360px] overflow-y-auto">
            <div role="row" className={`sticky top-0 z-10 grid ${onRemovePack ? PACK_COLUMNS_REMOVABLE : PACK_COLUMNS} gap-2 bg-[var(--prepacks-bg)] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[var(--prepacks-muted)]`}>
              <span role="columnheader">Pack</span><span role="columnheader" className="text-right">Odds</span><span role="columnheader">Status</span><span role="columnheader">On</span>{onRemovePack ? <span role="columnheader"><span className="sr-only">Remove</span></span> : null}
            </div>
            {packs.map((pack) => (
              <PackRow key={pack.id} pack={pack} recipeEnabled={recipe.enabled} canConfigure={canConfigure} busy={busy} onToggle={(enabled) => onTogglePacks([pack.id], enabled)} onOpen={() => onOpenPack(pack)} onRemove={onRemovePack && pack.pack ? () => onRemovePack(pack.pack) : null} removable={Boolean(onRemovePack)} />
            ))}
            {packs.length === 0 ? <p className="px-3 py-3 text-[11px] text-[var(--prepacks-muted)]">{packSearch ? 'No pack matches.' : 'No published packs from this schedule on this surface.'}</p> : null}
          </div>
        </div>
      ) : null}
    </li>
  )
}

function PackRow({ pack, recipeEnabled, canConfigure, busy, onToggle, onOpen, onRemove, removable }) {
  const tone = STATUS_TONE[pack.status] || STATUS_TONE.available
  const detail = pack.status === 'live' ? `Live at #${pack.position}${pack.pinned ? ', pinned' : ''}` : lineupReasonLabel(pack.status, pack.reason, pack.subject)
  return (
    <div role="row" className={`grid ${removable ? PACK_COLUMNS_REMOVABLE : PACK_COLUMNS} items-center gap-2 border-t border-[var(--prepacks-border)] px-3 py-1.5 text-xs`}>
      <span role="cell" className="min-w-0">
        <button type="button" onClick={onOpen} className="block w-full min-w-0 text-left hover:text-[var(--prepacks-accent-text)]" title={`Open ${pack.title}`}>
          <span className="flex items-center gap-1 truncate font-medium">{pack.pinned ? <Pin className="h-3 w-3 shrink-0 text-[var(--prepacks-accent-text)]" aria-label="Pinned" /> : null}<span className="truncate">{pack.title}</span></span>
          <span className="block truncate text-[11px] text-[var(--prepacks-muted)]">{pack.summary || '—'}{pack.book ? ` · ${pack.book}` : ''}</span>
        </button>
      </span>
      <span role="cell" className="text-right font-semibold tabular-nums text-[var(--wf-color-success)]">{pack.odds}</span>
      <span role="cell" className={`flex min-w-0 items-center gap-1 ${tone.text}`} title={detail}>
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${tone.dot}`} aria-hidden="true" />
        <span className="truncate">{LINEUP_STATUS_LABELS[pack.status] || pack.status}</span>
      </span>
      <span role="cell"><LineupSwitch label={`${pack.title} eligible on this surface`} checked={pack.enabled} disabled={!canConfigure || busy || !recipeEnabled} onChange={onToggle} /></span>
      {removable ? <span role="cell">{onRemove ? <button type="button" disabled={busy} onClick={onRemove} aria-label={`Remove ${pack.title} from this surface`} title="Remove from surface" className="rounded p-1 text-[var(--prepacks-muted)] hover:text-[var(--wf-color-danger)] disabled:opacity-40"><Trash2 className="h-3 w-3" /></button> : null}</span> : null}
    </div>
  )
}
